import { getDateTag, getT } from '@/i18n';
import { formatNumber, formatRouteDistance } from '@/lib/format';
import { useT, useUnitSystem } from '@/i18n/react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import {
  api,
  type AssistantConversationSummary,
  type AssistantForgetKind,
  type AssistantMemoryView,
} from '@/lib/api';
import { ASSISTANT_TEXT_MAX, classifyAssistantError, type PendingPlace, type PlaceRole } from '@/lib/routeAssistant';
import { useBackHandler } from '@/hooks/useBackHandler';
import { usePlaceSearch } from '@/hooks/usePlaceSearch';
import { useAssistantStore, type ChatEntry } from '@/store/assistantStore';
import type { ActivityType } from '@/types';
import './assistantChat.css';

/**
 * „Grout” asszisztens — a chat (#79).
 *
 * Az App szintjén él, hogy a Home-ról nyitva is túlélje az átlépést a rögzítés
 * képernyőre: a „Megnyitom a tervezőben” után a lap lecsúszik, alatta pedig a
 * tervező csúszik fel — ugyanaz a mozdulat oda-vissza.
 *
 * ⚠️ CSAK ELŐTÖLT: a tervezést továbbra is a felhasználó indítja.
 */

/** A lecsúszás ideje — a CSS `--ac-duration`-jével egyezik. */
const CLOSE_MS = 260;



export function AssistantChat() {
  const open = useAssistantStore((state) => state.open);
  /* A lecsúszás alatt még a DOM-ban marad. */
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      setClosing(false);
      return;
    }
    if (!rendered) return;
    setClosing(true);
    const timer = window.setTimeout(() => {
      setRendered(false);
      setClosing(false);
    }, CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  if (!rendered) return null;
  return createPortal(<ChatSheet closing={closing} />, document.body);
}

function ChatSheet({ closing }: { closing: boolean }) {
  const t = useT();
  const view = useAssistantStore((state) => state.view);
  const closeChat = useAssistantStore((state) => state.closeChat);
  const setView = useAssistantStore((state) => state.setView);
  const [menuOpen, setMenuOpen] = useState(false);

  /* Android vissza gomb: alnézetből a chatbe, a chatből be. */
  useBackHandler(!closing, () => {
    if (menuOpen) setMenuOpen(false);
    else if (view !== 'chat') setView('chat');
    else closeChat();
  });

  const title = view === 'history' ? t.assistantChat.history : view === 'memory' ? t.assistantChat.memory : 'Grout';

  return (
    <div
      className={`ac${closing ? ' ac--closing' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ac-title"
    >
      <header className="ac__head">
        {view !== 'chat' ? (
          <button type="button" className="ac__icon-btn" aria-label={t.assistantChat.back} onClick={() => setView('chat')}>
            <Icon name="chevron-left" size={20} />
          </button>
        ) : (
          <span className="ac__avatar" aria-hidden="true">
            <Icon name="chat" size={18} />
          </span>
        )}
        <div className="ac__title">
          <h2 id="ac-title">{title}</h2>
          {view === 'chat' ? <span className="ac__subtitle">{t.assistantChat.subtitle}</span> : null}
        </div>
        {view === 'chat' ? (
          <button
            type="button"
            className="ac__icon-btn"
            aria-label={t.assistantChat.menu}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <Icon name="more" size={20} />
          </button>
        ) : null}
        <button type="button" className="ac__icon-btn" aria-label={t.assistantChat.close} onClick={closeChat}>
          <Icon name="close" size={18} />
        </button>
        {menuOpen ? <ChatMenu onClose={() => setMenuOpen(false)} /> : null}
      </header>

      {view === 'chat' ? <ChatView /> : null}
      {view === 'history' ? <HistoryView /> : null}
      {view === 'memory' ? <MemoryView /> : null}
    </div>
  );
}

function ChatMenu({ onClose }: { onClose: () => void }) {
  const t = useT();
  const newConversation = useAssistantStore((state) => state.newConversation);
  const setView = useAssistantStore((state) => state.setView);
  const resetLocal = useAssistantStore((state) => state.resetLocal);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function withdraw() {
    setBusy(true);
    setError(null);
    try {
      await api.assistantConsentWithdraw();
      resetLocal();
      onClose();
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      setError(failure.kind === 'message' ? failure.text : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ac__menu" role="menu">
      <button type="button" role="menuitem" onClick={() => { newConversation(); onClose(); }}>
        {t.assistantChat.new}
      </button>
      <button type="button" role="menuitem" onClick={() => { setView('history'); onClose(); }}>
        {t.assistantChat.history}
      </button>
      <button type="button" role="menuitem" onClick={() => { setView('memory'); onClose(); }}>
        {t.assistantChat.memory}
      </button>
      {confirmWithdraw ? (
        <div className="ac__menu-confirm">
          <p>{t.assistantChat.withdrawHint}</p>
          <button type="button" className="ac__danger" disabled={busy} onClick={() => void withdraw()}>
            {busy ? t.assistantChat.wait : t.assistantChat.withdrawDelete}
          </button>
          {error ? <p className="ac__error">{error}</p> : null}
        </div>
      ) : (
        <button type="button" role="menuitem" className="ac__menu-danger" onClick={() => setConfirmWithdraw(true)}>
          {t.assistantChat.withdraw}
        </button>
      )}
    </div>
  );
}

/* ── Beszélgetés ─────────────────────────────────────────────────── */

function ChatView() {
  const t = useT();
  const units = useUnitSystem();
  const suggestions = [units === 'imperial' ? t.assistantChat.suggestionRunImperial : t.assistantChat.suggestionRun, t.assistantChat.suggestionWalk, t.assistantChat.suggestionRide];
  const entries = useAssistantStore((state) => state.entries);
  const busy = useAssistantStore((state) => state.busy);
  const error = useAssistantStore((state) => state.error);
  const quota = useAssistantStore((state) => state.quota);
  const consentPending = useAssistantStore((state) => state.consentPending);
  const send = useAssistantStore((state) => state.send);
  const listRef = useRef<HTMLDivElement>(null);

  /* Mindig az utolsó üzenet látszik. */
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [entries, busy, consentPending]);

  const lastReady = [...entries].reverse().find((entry) => entry.role === 'assistant')?.ready === true;

  return (
    <>
      <div className="ac__list" ref={listRef} aria-live="polite">
        {entries.length === 0 && !consentPending ? (
          <div className="ac__empty">
            <p className="ac__bubble ac__bubble--geri">
              {t.assistantChat.greeting}
            </p>
            <ul className="ac__suggestions">
              {suggestions.map((suggestion) => (
                <li key={suggestion}>
                  <button type="button" disabled={busy} onClick={() => void send(suggestion, 'chat')}>
                    {suggestion}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {entries.map((entry, index) => (
          <ChatBubble key={entry.id} entry={entry} latest={index === entries.length - 1} />
        ))}

        {consentPending ? <ConsentCard /> : null}
        {busy ? (
          <p className="ac__typing" role="status" aria-label={t.assistantChat.thinking}>
            <span />
            <span />
            <span />
          </p>
        ) : null}
        {error ? <p className="ac__error">{error}</p> : null}
      </div>

      {lastReady ? <PlannerButton /> : null}

      <Composer disabled={busy || consentPending !== null} quota={quota} />
    </>
  );
}

function ChatBubble({ entry, latest }: { entry: ChatEntry; latest: boolean }) {
  const t = useT();
  const chooseActivity = useAssistantStore((state) => state.chooseActivity);
  const answerFollowUp = useAssistantStore((state) => state.answerFollowUp);
  const send = useAssistantStore((state) => state.send);
  const busy = useAssistantStore((state) => state.busy);
  if (entry.role === 'user') return <p className="ac__bubble ac__bubble--me">{entry.text}</p>;
  if (entry.followUp) {
    /* Továbbkérdezés (#79): a válasz a felhasználó buborékja lesz, mint egy beszélgetésben. */
    return (
      <>
        <div className="ac__geri">
          <p className="ac__bubble ac__bubble--geri">{entry.text}</p>
          {entry.answered === undefined && latest ? (
            <div className="ac__quick" role="group" aria-label={entry.text}>
              {entry.followUp.options.map((option) => (
                <button key={option.value} type="button" onClick={() => answerFollowUp(entry.id, option.value)}>
                  {option.label}
                </button>
              ))}
              <button type="button" className="ac__quick-skip" onClick={() => answerFollowUp(entry.id, null)}>
                {t.assistantChat.any}
              </button>
            </div>
          ) : null}
        </div>
        {entry.answered !== undefined ? <p className="ac__bubble ac__bubble--me">{entry.answered}</p> : null}
      </>
    );
  }
  return (
    <div className="ac__geri">
      <p className="ac__bubble ac__bubble--geri">{entry.text}</p>
      {entry.applied?.length ? <AppliedChips applied={entry.applied} /> : null}
      {entry.pending?.map((place) => <PlaceChooser key={String(place.role)} place={place} />)}
      {entry.notes?.map((note) => (
        <p key={note} className="ac__note">{note}</p>
      ))}
      {entry.remembered?.length ? (
        <p className="ac__remembered">{t.assistantChat.remembered} {entry.remembered.join(', ')}</p>
      ) : null}
      {/* #87: a modell gyorsválaszai — koppintásra üzenetként mennek el. Csak a legutolsónál. */}
      {entry.quickReplies?.length && latest && !entry.pending?.length ? (
        <div className="ac__quick" role="group" aria-label={t.assistantChat.quickReplies}>
          {entry.quickReplies.map((quick) => (
            <button key={quick} type="button" disabled={busy} onClick={() => void send(quick, 'chat')}>
              {quick}
            </button>
          ))}
        </div>
      ) : null}
      {entry.needsActivity && latest ? (
        <div className="ac__quick" role="group" aria-label={t.assistantChat.activity}>
          <span>{t.assistantChat.activityQuestion}</span>
          {(
            [
              ['run', t.assistantChat.run],
              ['walk', t.assistantChat.walk],
              ['ride', t.assistantChat.ride],
            ] as [ActivityType, string][]
          ).map(([type, label]) => (
            <button key={type} type="button" onClick={() => chooseActivity(entry.id, type)}>
              {label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AppliedChips({ applied }: { applied: string[] }) {
  const t = useT();
  return (
    <ul className="ac__chips" aria-label={t.assistantChat.applied}>
      {applied.map((chip) => (
        <li key={chip} className="ac__chip">
          <Icon name="check" size={14} />
          {chip}
        </li>
      ))}
    </ul>
  );
}

/** Több találatnál a felhasználó választ — a választást Grout megjegyzi. */
/** Ennyi jelölt látszik először; a többi a „Több találat” mögött (#79). */
const CHOOSER_VISIBLE = 3;

export function PlaceChooser({ place }: { place: PendingPlace }) {
  const t = useT();
  useUnitSystem();
  const choosePlace = useAssistantStore((state) => state.choosePlace);
  const [expanded, setExpanded] = useState(false);
  const [searching, setSearching] = useState(false);
  /* Egy találatnál is rákérdezünk — a kereső tévedhet (Geri, #79). */
  const single = place.candidates.length === 1;
  const shown = expanded ? place.candidates : place.candidates.slice(0, CHOOSER_VISIBLE);
  const more = place.candidates.length - shown.length;

  if (searching) return <PlaceSearchBox place={place} onBack={() => setSearching(false)} />;

  return (
    <div className="ac__choose">
      <p className="ac__choose-title">
        {roleLabel(place.role)}: {single ? t.assistantChat.guessedPlace(place.name) : t.assistantChat.whichPlace(place.name)}
      </p>
      <ul className="ac__choose-list">
        {shown.map((hit) => (
          <li key={`${hit.lat},${hit.lng}`}>
            <button type="button" onClick={() => choosePlace(place, { lat: hit.lat, lng: hit.lng, label: hit.label })}>
              <span className="ac__choose-label">{hit.label}</span>
              <span className="ac__choose-distance">
                {formatRouteDistance(hit.distanceM)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="ac__choose-actions">
        {more > 0 ? (
          <button type="button" className="ac__choose-none" onClick={() => setExpanded(true)}>
            {t.assistantChat.more(formatNumber(more))}
          </button>
        ) : null}
        {/* „Egyik sem” → kereső: begépeli pontosabban, és a teljes listából választ. */}
        <button type="button" className="ac__choose-none" onClick={() => setSearching(true)}>
          {single ? t.assistantChat.notThis : t.assistantChat.none}
        </button>
      </div>
    </div>
  );
}

/** Részletes kereső a chatben — ugyanaz a címkereső, mint a tervezőé. */
function PlaceSearchBox({ place, onBack }: { place: PendingPlace; onBack: () => void }) {
  const t = useT();
  useUnitSystem();
  const choosePlace = useAssistantStore((state) => state.choosePlace);
  const rejectPlace = useAssistantStore((state) => state.rejectPlace);
  const near = useAssistantStore((state) => state.lastNear);
  const [query, setQuery] = useState(place.name);
  const { hits, searching } = usePlaceSearch(query, near);

  return (
    <div className="ac__choose">
      <p className="ac__choose-title">{roleLabel(place.role)}{t.assistantChat.searchPrecise}</p>
      <input
        type="search"
        className="ac__search"
        aria-label={t.assistantChat.searchRole(roleLabel(place.role))}
        placeholder={t.assistantChat.searchPlaceholder}
        value={query}
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />
      {searching && hits.length === 0 ? <p className="ac__muted">{t.assistantChat.searching}</p> : null}
      {!searching && query.trim().length >= 3 && hits.length === 0 ? (
        <p className="ac__muted">{t.assistantChat.noResults}</p>
      ) : null}
      {hits.length ? (
        <ul className="ac__choose-list">
          {hits.map((hit) => (
            <li key={`${hit.lat},${hit.lng}`}>
              <button type="button" onClick={() => choosePlace(place, { lat: hit.lat, lng: hit.lng, label: hit.label })}>
                <span className="ac__choose-label">{hit.label}</span>
                <span className="ac__choose-distance">
                  {formatRouteDistance(hit.distanceM)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="ac__choose-actions">
        <button type="button" className="ac__choose-none" onClick={onBack}>
          {t.assistantChat.backResults}
        </button>
        <button type="button" className="ac__choose-none" onClick={() => rejectPlace(place)}>
          {t.assistantChat.notHere}
        </button>
      </div>
    </div>
  );
}

function roleLabel(role: PlaceRole): string {
  if (role === 'from') return getT().assistantChat.start;
  if (role === 'to') return getT().assistantChat.end;
  return getT().assistantChat.stop(role + 1);
}

function PlannerButton() {
  const t = useT();
  const goToPlanner = useAssistantStore((state) => state.goToPlanner);
  const navigate = useNavigate();
  return (
    <div className="ac__cta">
      <button
        type="button"
        className="ac__primary"
        onClick={() => {
          if (goToPlanner() === 'navigate') navigate('/rogzites');
        }}
      >
        <Icon name="pin" size={18} />
        {t.assistantChat.planner}
      </button>
    </div>
  );
}

function ConsentCard() {
  const t = useT();
  const busy = useAssistantStore((state) => state.busy);
  const acceptConsent = useAssistantStore((state) => state.acceptConsent);
  const declineConsent = useAssistantStore((state) => state.declineConsent);
  return (
    <div className="ac__consent">
      <p>
        {t.assistantChat.consentBefore} <strong>Google Gemini</strong> {t.assistantChat.consentProcessing} <strong>{t.assistantChat.consentSaved}</strong>{t.assistantChat.consentAfter}
      </p>
      <p>
        {t.assistantChat.consentVoice}
      </p>
      <p className="ac__muted">
        {t.assistantChat.consentControl}
      </p>
      <div className="ac__consent-actions">
        <button type="button" className="ac__primary" disabled={busy} onClick={() => void acceptConsent()}>
          {busy ? t.assistantChat.wait : t.assistantChat.accept}
        </button>
        <button type="button" className="ac__ghost" disabled={busy} onClick={declineConsent}>
          {t.assistantChat.notNow}
        </button>
      </div>
    </div>
  );
}

function Composer({ disabled, quota }: { disabled: boolean; quota: string | null }) {
  const t = useT();
  const send = useAssistantStore((state) => state.send);
  const [text, setText] = useState('');
  const canSend = text.trim().length > 0 && !disabled;

  async function submit() {
    if (!canSend) return;
    const value = text;
    setText('');
    const ok = await send(value, 'chat');
    /* Hiba esetén a szöveg visszakerül, ne kelljen újragépelni. */
    if (!ok) setText((current) => current || value);
  }

  return (
    <form
      className="ac__composer"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {quota ? <p className="ac__quota">{quota}</p> : null}
      <div className="ac__composer-row">
        <textarea
          className="ac__input"
          rows={1}
          maxLength={ASSISTANT_TEXT_MAX}
          enterKeyHint="send"
          placeholder={t.assistantChat.placeholder}
          aria-label={t.assistantChat.message}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <button type="submit" className="ac__send" aria-label={t.assistantChat.send} disabled={!canSend}>
          <Icon name="send" size={20} />
        </button>
      </div>
    </form>
  );
}

/* ── Előzmények ──────────────────────────────────────────────────── */

function dateFormat() { return new Intl.DateTimeFormat(getDateTag(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }

function HistoryView() {
  const t = useT();
  const openConversation = useAssistantStore((state) => state.openConversation);
  const currentId = useAssistantStore((state) => state.conversationId);
  const newConversation = useAssistantStore((state) => state.newConversation);
  const resetLocal = useAssistantStore((state) => state.resetLocal);
  const [items, setItems] = useState<AssistantConversationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .assistantConversations()
      .then((result) => alive && setItems(result.conversations))
      .catch((problem: unknown) => {
        if (!alive) return;
        const failure = classifyAssistantError(problem);
        setError(failure.kind === 'message' ? failure.text : getT().assistantChat.consentFirst);
        setItems([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function remove(id: string) {
    setItems((list) => list?.filter((item) => item.id !== id) ?? null);
    if (id === currentId) newConversation();
    await api.assistantDeleteConversation(id).catch(() => undefined);
  }

  async function removeAll() {
    try {
      await api.assistantDeleteHistory();
      resetLocal();
      setItems([]);
      setConfirmAll(false);
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      setError(failure.kind === 'message' ? failure.text : null);
    }
  }

  return (
    <div className="ac__panel">
      {items === null ? <p className="ac__muted">{t.assistantChat.loading}</p> : null}
      {items?.length === 0 && !error ? <p className="ac__muted">{t.assistantChat.noHistory}</p> : null}
      {error ? <p className="ac__error">{error}</p> : null}
      <ul className="ac__rows">
        {items?.map((item) => (
          <li key={item.id} className={item.id === currentId ? 'ac__row ac__row--current' : 'ac__row'}>
            <button type="button" className="ac__row-main" onClick={() => void openConversation(item.id)}>
              <span className="ac__row-title">{item.title || t.assistantChat.conversation}</span>
              <span className="ac__row-meta">
                {dateFormat().format(new Date(item.updatedAt))} · {t.assistantChat.messageCount(formatNumber(item.messageCount))}
              </span>
            </button>
            <button
              type="button"
              className="ac__icon-btn"
              aria-label={t.assistantChat.deleteTitle(item.title)}
              onClick={() => void remove(item.id)}
            >
              <Icon name="trash" size={18} />
            </button>
          </li>
        ))}
      </ul>
      {items?.length ? (
        confirmAll ? (
          <div className="ac__menu-confirm">
            <p>{t.assistantChat.deleteWarning}</p>
            <button type="button" className="ac__danger" onClick={() => void removeAll()}>
              {t.assistantChat.deleteAll}
            </button>
          </div>
        ) : (
          <button type="button" className="ac__link" onClick={() => setConfirmAll(true)}>
            {t.assistantChat.clearHistory}
          </button>
        )
      ) : null}
    </div>
  );
}

/* ── Memória ─────────────────────────────────────────────────────── */

const ACTIVITY_LABEL: Record<string, string> = { get run() { return getT().assistantChat.runLower; }, get walk() { return getT().assistantChat.walkLower; }, get bike() { return getT().assistantChat.rideLower; } };
const PREF_LABEL: Record<string, string> = {
  get 'mode:loop'() { return getT().assistantChat.loopLower; },
  get 'mode:direct'() { return getT().assistantChat.directLower; },
  get 'detour:small'() { return getT().assistantChat.detourSmall; },
  get 'detour:medium'() { return getT().assistantChat.detourMedium; },
  get 'detour:large'() { return getT().assistantChat.detourLarge; },
  get 'preference:quiet'() { return getT().assistantChat.quiet; },
  get 'preference:protected'() { return getT().assistantChat.protected; },
  get 'preference:fast'() { return getT().assistantChat.fast; },
  get 'terrain:flat'() { return getT().assistantChat.flat; },
  get 'terrain:hilly'() { return getT().assistantChat.hilly; },
  get 'terrain:balanced'() { return getT().assistantChat.balanced; },
  get cycleways() { return getT().assistantChat.cycleways; },
};

function counts(record: Record<string, number | undefined>, labels: Record<string, string>): string {
  return Object.entries(record)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && labels[entry[0]] !== undefined)
    .sort((a, b) => b[1] - a[1])
    .map(([key, count]) => `${labels[key]} (${formatNumber(count)}×)`)
    .join(', ');
}

function MemoryView() {
  const t = useT();
  const [memory, setMemory] = useState<AssistantMemoryView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .assistantMemory()
      .then((result) => alive && setMemory(result.memory))
      .catch((problem: unknown) => {
        if (!alive) return;
        const failure = classifyAssistantError(problem);
        setError(failure.kind === 'message' ? failure.text : null);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function forget(kind: AssistantForgetKind, key = '') {
    try {
      const result = await api.assistantForget(kind, key);
      setMemory(result.memory);
    } catch (problem) {
      const failure = classifyAssistantError(problem);
      setError(failure.kind === 'message' ? failure.text : null);
    }
  }

  if (error) return <div className="ac__panel"><p className="ac__error">{error}</p></div>;
  if (!memory) return <div className="ac__panel"><p className="ac__muted">{t.assistantChat.loading}</p></div>;

  const habits = [counts(memory.activities, ACTIVITY_LABEL), counts(memory.prefs, PREF_LABEL)].filter(Boolean).join(' · ');
  const empty = !habits && !memory.places.length && !memory.facts.length && !memory.recent.length;

  return (
    <div className="ac__panel">
      <p className="ac__muted">
        {t.assistantChat.memoryHint}
      </p>
      {empty ? <p className="ac__muted">{t.assistantChat.emptyMemory}</p> : null}

      {memory.facts.length ? (
        <MemorySection title={t.assistantChat.facts}>
          {memory.facts.map((fact) => (
            <MemoryRow key={fact.text} text={fact.text} onForget={() => void forget('fact', fact.text)} />
          ))}
        </MemorySection>
      ) : null}

      {memory.places.length ? (
        <MemorySection title={t.assistantChat.places}>
          {memory.places.map((place) => (
            <MemoryRow
              key={place.name}
              text={place.name}
              meta={place.label}
              onForget={() => void forget('place', place.name)}
            />
          ))}
        </MemorySection>
      ) : null}

      {habits ? (
        <MemorySection title={t.assistantChat.habits}>
          <MemoryRow text={habits} onForget={() => void forget('habits')} />
        </MemorySection>
      ) : null}

      {memory.recent.length ? (
        <MemorySection title={t.assistantChat.recent}>
          {[...memory.recent].reverse().map((item) => (
            <MemoryRow key={`${item.at}${item.text}`} text={item.text} onForget={() => void forget('recent', item.text)} />
          ))}
        </MemorySection>
      ) : null}
    </div>
  );
}

function MemorySection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="ac__memory">
      <h3>{title}</h3>
      <ul className="ac__rows">{children}</ul>
    </section>
  );
}

function MemoryRow({ text, meta, onForget }: { text: string; meta?: string; onForget: () => void }) {
  const t = useT();
  return (
    <li className="ac__row">
      <span className="ac__row-main ac__row-main--static">
        <span className="ac__row-title">{text}</span>
        {meta ? <span className="ac__row-meta">{meta}</span> : null}
      </span>
      <button type="button" className="ac__icon-btn" aria-label={t.assistantChat.forget(text)} onClick={onForget}>
        <Icon name="trash" size={18} />
      </button>
    </li>
  );
}
